import React from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';

import type { AutoMergeCandidate } from '../../types';

import { AutoMergeCandidateCard } from './AutoMergeCandidateCard';

interface AutoMergeReviewPanelProps {
  candidates: AutoMergeCandidate[];
  deselectedGroups: Set<string>;
  approvedCount: number;
  skippedGroups: number;
  onToggleGroup: (groupId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const AutoMergeReviewPanel: React.FC<AutoMergeReviewPanelProps> = ({
  candidates,
  deselectedGroups,
  approvedCount,
  skippedGroups,
  onToggleGroup,
  onSelectAll,
  onDeselectAll,
  onConfirm,
  onCancel,
}) => {
  const allSelected = deselectedGroups.size === 0;
  const noneSelected = deselectedGroups.size === candidates.length;

  return (
    <Box>
      {/* Summary */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Found {candidates.length} group{candidates.length !== 1 ? 's' : ''} safe for auto-merge
        {skippedGroups > 0 && (
          <> ({skippedGroups} group{skippedGroups !== 1 ? 's' : ''} skipped — need manual review)</>
        )}
      </Alert>

      {/* Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && !noneSelected}
              onChange={allSelected ? onDeselectAll : onSelectAll}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </Typography>
          }
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={approvedCount === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Merge {approvedCount} Group{approvedCount !== 1 ? 's' : ''}
          </Button>
        </Box>
      </Box>

      {/* Candidate Cards */}
      {candidates.map((candidate, index) => (
        <AutoMergeCandidateCard
          key={candidate.group_id}
          candidate={candidate}
          isSelected={!deselectedGroups.has(candidate.group_id)}
          onToggle={() => onToggleGroup(candidate.group_id)}
          index={index}
        />
      ))}
    </Box>
  );
};
